'use client'

import useSwr from 'swr'
import { API_URL } from '@/utils/constants'

/// What a Twetch picture IS, when it is a Bitcoin inscription
/// (`ord://<inscriptionId>`): its name, number and collection, from the same
/// inscription record the rest of the site shows.
export interface TwetchPfp {
	id: string
	num: number | null
	name: string | null
	collection: { name: string; slug: string } | null
}

const ORD_REF = /^ord:\/\/([a-f0-9]{64}i\d{1,10})$/i

export function pfpInscriptionId(icon: string | null | undefined): string | null {
	return ORD_REF.exec((icon ?? '').trim())?.[1].toLowerCase() ?? null
}

interface InscriptionRecord {
	id: string
	num?: number
	meta?: { name?: string | null } | null
	collection?: { name?: string | null; slug?: string | null } | null
}

/// Null while loading, when the picture is not an inscription, or when the
/// record can't be read — callers simply show nothing extra.
export default function useTwetchPfp(icon: string | null | undefined, enabled = true): TwetchPfp | null {
	const id = pfpInscriptionId(icon)
	const { data } = useSwr<InscriptionRecord>(
		enabled && id ? `${API_URL}/inscription/${id}` : null,
		async (url: string) => {
			const res = await fetch(url)
			if (!res.ok) throw new Error('inscription unavailable')
			return res.json()
		},
		{ revalidateOnFocus: false, dedupingInterval: 600_000 }
	)
	if (!id || !data) {
		return null
	}
	const slug = data.collection?.slug
	const collectionName = data.collection?.name
	return {
		id,
		num: typeof data.num === 'number' ? data.num : null,
		name: data.meta?.name?.trim() || null,
		collection: slug && collectionName ? { name: collectionName, slug } : null
	}
}
